/** Shared SSE subscription hook that both useChatGeneration and useFileProcessing reuse. */

import { useEffect, useRef } from "react";
import { fetchEventSource } from "@microsoft/fetch-event-source";
import { isSessionRefusal, markSessionExpired } from "@/lib/session-expiry";
import { apiFetch } from "@/services/api-client";

export interface UseSseSubscriptionOptions {
  /** Full URL to the SSE endpoint. */
  url: string | null;
  /**
   * Called for every SSE message with the raw JSON string (not pre-parsed), and
   * the event name it arrived under. One stream can carry more than one kind of
   * event, and a consumer that reads them all as its own logs a warning per
   * event it was never meant to handle.
   */
  onMessage: (data: string, event: string) => void;
  /** Called when the SSE connection fails (after auth retry). */
  onError?: (err: unknown) => void;
}

/**
 * Subscribe to an SSE endpoint. The connection is opened when `url` is
 * non-null and closed on unmount or when `url` changes.
 *
 * Returns a ref-stable abort function callers can invoke to close early.
 */
export function useSseSubscription({
  url,
  onMessage,
  onError,
}: UseSseSubscriptionOptions): () => void {
  const abortRef = useRef<AbortController | null>(null);
  const onMessageRef = useRef(onMessage);
  const onErrorRef = useRef(onError);
  onMessageRef.current = onMessage;
  onErrorRef.current = onError;

  useEffect(() => {
    if (!url) return;

    const ctrl = new AbortController();
    abortRef.current = ctrl;

    let retryCount = 0;
    // Long-running generations (30+ min) require many reconnections due to
    // the 10-min server-side stream limit. Allow generous consecutive retries
    // so transient network issues don't kill the stream permanently.
    const MAX_RETRIES = 20;

    // Nothing here refreshes anything. The session is a cookie the browser
    // attaches to a same-origin request by itself, and the API refreshes the
    // access token underneath the stream, so a token never reaches this side
    // to expire. This replaces both a custom fetch and a pre-expiry timer.

    void fetchEventSource(url, {
      signal: ctrl.signal,
      // fetchEventSource retries by default; openWhenHidden keeps the
      // connection alive when the tab is backgrounded.
      openWhenHidden: true,
      // The one transport. `apiFetch` owns the credentials mode, so the stream
      // inherits it rather than restating it and drifting from it later.
      fetch: (input, init) => apiFetch(input as string, init),

      // Returns the promise the library's signature asks for without being
      // async, since there is nothing to await now that a refused stream no
      // longer reacquires a token.
      onopen(response) {
        if (response.ok) {
          retryCount = 0;
          return Promise.resolve();
        }
        // A 401 means the session itself has gone, not that a token went
        // stale. Retrying cannot help, and a generation long enough to
        // outlive its session is exactly the case with no other API traffic
        // to notice, so this path has to report it or nothing does.
        if (isSessionRefusal(response.status)) {
          markSessionExpired();
          ctrl.abort();
          return Promise.resolve();
        }
        throw new Error(`SSE open failed: ${response.status}`);
      },

      // When the server closes the connection cleanly (e.g. 10-min
      // MAX_STREAM_DURATION_SECONDS timeout), fetchEventSource treats it
      // as success and resolves the promise — no retry. Throwing from
      // onclose forces the error path, triggering onerror → retry with
      // Last-Event-ID so no events are lost.
      onclose: () => {
        throw new Error("Stream closed by server");
      },

      onmessage: (msg) => {
        if (!msg.data) return;
        onMessageRef.current(msg.data, msg.event);
      },

      onerror: (err) => {
        if (ctrl.signal.aborted) return;
        retryCount++;
        console.warn(`SSE error (retry ${retryCount}/${MAX_RETRIES}):`, err);

        if (retryCount >= MAX_RETRIES) {
          onErrorRef.current?.(err);
          ctrl.abort();
          return;
        }

        // Exponential backoff capped at 16s: 1s, 2s, 4s, 8s, 16s, 16s…
        return Math.min(1000 * 2 ** (retryCount - 1), 16_000);
      },
    });

    return () => {
      ctrl.abort();
    };
  }, [url]);

  return () => abortRef.current?.abort();
}
